import { useState, useCallback, useEffect } from 'react';
import { applyApiError, useLoaderState } from '../helpers/data-loader';
import axios from 'axios';

/**
 * Custom hook for paginated data that extends useData with pagination parameters
 * @param {string} endpoint - The API endpoint
 * @param {Object} dataOptions - Options for data loading (same as useData)
 * @param {number} initialPageSize - Initial page size (default: 30)
 * @param {number} initialPage - Initial page number (default: 1)
 * @returns {Array} [data, fetchData, loading, error, pagination, setPagination, handlePageChange]
 */
interface PaginationState {
  page: number;
  pageSize: number;
  sortColumn: string | null;
  sortDesc: boolean;
}

interface PageChangeArgs {
  pageIndex: number;
  pageSize: number;
  sortColumn?: string;
  sortDesc?: boolean;
}

export const usePaginatedData = (
  endpoint: string,
  dataOptions: Record<string, any> = {},
  initialPageSize = 30,
  initialPage = 1,
  initialSortColumn: string | null = null,
  initialSortDesc = false,
) => {
  const [pagination, setPagination] = useState<PaginationState>({
    page: initialPage,
    pageSize: initialPageSize,
    sortColumn: initialSortColumn,
    sortDesc: initialSortDesc,
  });

  const [data, loading, error, onLoad, onError, onLoading] = useLoaderState({
    loading: dataOptions.initialLoading,
    data: dataOptions.initialData || [],
  });

  // Build endpoint with pagination parameters
  const buildEndpoint = useCallback(
    (paginationParams: PaginationState = pagination) => {
      const { page, pageSize, sortColumn, sortDesc } = paginationParams;
      const skip = (page - 1) * pageSize;
      const limit = pageSize;

      // Check if endpoint already has query parameters
      const separator = endpoint.includes('?') ? '&' : '?';
      const params = new URLSearchParams();

      params.append('skip', skip.toString());
      params.append('limit', limit.toString());

      // Add sorting parameters if available
      if (sortColumn) {
        params.append('sortColumn', sortColumn);
        params.append('sortDesc', sortDesc ? 'true' : 'false');
      }

      return `${endpoint}${separator}${params.toString()}`;
    },
    [endpoint, pagination],
  );

  // Handle errors consistently with useData
  const handleError = useCallback(
    (realError: any) => applyApiError(realError, onError, dataOptions.errorMsg),
    [dataOptions.errorMsg, onError],
  );

  // Fetch data function
  const fetchData = useCallback(
    async (customPagination: PaginationState | null = null) => {
      const paginationToUse = customPagination || pagination;
      const endpointWithParams = buildEndpoint(paginationToUse);

      onLoading();

      try {
        const axiosResponse =
          await axios[dataOptions.reqType || 'get'](endpointWithParams);

        if (axiosResponse.status === 200) {
          const { data: resp } = axiosResponse;
          const responseData = resp.current_page ? resp : resp.data || resp;
          onLoad(responseData);
        } else {
          handleError(axiosResponse);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [
      pagination,
      buildEndpoint,
      onLoading,
      onLoad,
      handleError,
      dataOptions.reqType,
    ],
  );

  // Handle page changes from the table component
  const handlePageChange = useCallback(
    ({ pageIndex, pageSize, sortColumn, sortDesc }: PageChangeArgs) => {
      const newPagination = {
        page: pageIndex + 1, // react-table uses 0-based indexing
        pageSize,
        sortColumn: sortColumn || null,
        sortDesc: sortDesc || false,
      };

      setPagination(newPagination);
    },
    [],
  );

  // Fetch data when pagination OR the endpoint changes. `endpoint` is a
  // primitive so it can't cause a render loop, and including it ensures a
  // refetch when the caller points the table at a different endpoint.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endpoint,
    pagination.page,
    pagination.pageSize,
    pagination.sortColumn,
    pagination.sortDesc,
  ]);

  return [
    data,
    fetchData,
    loading,
    error,
    pagination,
    setPagination,
    handlePageChange,
  ];
};
